import { useState } from 'react';
import { Autocomplete, DialogContent, DialogContentText, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FormDialogContainer from '../../../../Components/FormDialogContainer/FormDialogContainer';
import { DialogActionButtons } from '../../../../Components/DialogActionButtons/DialogActionButtons';
import { ROLES } from '../../../../Types/constants';
import { Loader } from '../../../../Components/Loader/Loader';

interface UserFormDialogProps {
  open: boolean;
  handleClose: () => void;
  users: User[];
  onAddUser: (user: User) => Promise<void> | void;
  fromTA?: boolean;
}

export default function UserFormDialog({
  open,
  handleClose,
  users,
  onAddUser,
  fromTA,
}: UserFormDialogProps) {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleUserSelect = (event: any, newValue: User | null) => {
    setSelectedUser(newValue);
  };

  const handleAddClick = async () => {
    if (selectedUser) {
      setIsLoading(true);  

      try {
        await onAddUser(selectedUser);  
      } catch (error) {
        console.error('Error adding user:', error);
      } finally {
        setIsLoading(false);  
        handleClose();  
      }
    }
  };

  const getMappedLabelRoles = (userRoles: string[]): string => {

    if (!userRoles) return '';

    const userRole = userRoles[0];

    switch (userRole) {
      case ROLES.admin:
        return t('userRoles.admin');
      case ROLES.recruiter:
        return t('userRoles.recruiter');;
      case ROLES.internal:
        return t('userRoles.internal');;
      case ROLES.operations:
        return t('userRoles.operations');;
      default:
        return '';
    }
  };

  return (
    <FormDialogContainer open={open} handleClose={handleClose} title={t('jobs.team.select_user')}>
      <DialogContent>
        <DialogContentText style={{ marginBottom: 16 }}>
          {t(fromTA ? 'jobs.team.description_ta' : 'jobs.team.description_others')}
        </DialogContentText>
        <Autocomplete
          options={
            fromTA
              ? users.filter((user) => user.roles.includes(ROLES.admin) || user.roles.includes(ROLES.recruiter))
              : users.filter((user) => user.roles.includes(ROLES.internal) || user.roles.includes(ROLES.operations))
          }
          getOptionLabel={(option) => `${option.fullName} - ${getMappedLabelRoles(option.roles)}`}
          onChange={handleUserSelect}
          renderInput={(params) => <TextField {...params} label={t('common.user')} />}
          fullWidth
        />
      </DialogContent>
      <DialogActionButtons
        onCancel={handleClose}
        onConfirm={handleAddClick}
        disabled={isLoading}
      />
      <Loader isLoading={isLoading} />
    </FormDialogContainer>
  );
}
